import { useMutation, useQuery } from "@apollo/client";
import { HeaderActionType, TableStandard, TableToolBarStacked, useManageModal, ApiValidationError, mapApiFieldValidationErrors, SelectOption, TableToolBar, StandardFilterOption } from "ui";
import { GET_PEOPLE_WITH_SESSION_COUNT } from "../../../queries";
import { useEffect, useState } from "react";
import { GridColDef, GridRowId, GridRowModel } from "@mui/x-data-grid";
import { format } from "date-fns";
import { Box, Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { orderBySessionsRun } from "../../../tools";
import { useParams } from "react-router";
import { Add, Close, FolderShared, People } from "@mui/icons-material";

import { CompanyDataType, PersonDataType } from "../../../types";
const TABLE_COLUMNS: GridColDef[] = [
    {
        field: "firstName", headerName: "First Name", flex: 1, sortable: true, resizable: false
    }, {
        field: "lastName", headerName: "Last Name", flex: 1, sortable: true, resizable: false
    },
    {
        field: "locations", headerName: "Location(s)", flex: 2, sortable: true, resizable: false,
        renderCell: ({ value }) => {
            const hasLocations = value.length > 0
            if (hasLocations) {
                return (
                    <Grid container spacing={1} sx={{ paddingTop: 2, paddingBottom: 2, justifyContent: "start" }}>
                        {value.map((location: string) => (
                            <Grid key={`tags-chip-${location}`} style={{ marginTop: 1 }}>
                                <Chip className="chip" label={location} />
                            </Grid>
                        ))}
                    </Grid>
                )
            } else {
                return (<Typography>Please assign person to a location</Typography>)
            }
        }
    },
    {
        field: "totalCompleteSessions", headerName: "Total Complete Sessions", flex: 1, sortable: true, resizable: false,
        renderHeader: () => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "start", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>Total Complete Sessions</div>
        )
    }, {
        field: "lastRunSession", headerName: "Date of Last Run Session", flex: 1, sortable: true, resizable: false,
        renderHeader: () => (
            <div style={{ whiteSpace: "break-spaces", textAlign: "start", fontWeight: 500, lineHeight: 1, justifyContent: "center", width: "100%" }}>Date of Last Run Session</div>
        ), renderCell: ({ value }) => {
            return value === "0001-01-01T00:00:00.000-05:00" ? "N/A" : format(new Date(value), "MM/dd/yyyy")
        }
    },

]

function mapToTable(data: any) {
    const node = data.node
    const _data = {
        ...node,
        locations: node.locations?.edges?.map((x: any) => x.node.name),
        sessionsReviewed: node.workLists.filter((x: any) => x.status === "Read").length

    }
    return _data
}
export type StaffProgressHeaderActionType = "SLIDEIN" | "MODAL_CAP" | "MODAL_GENERAL" | "ADD_USER"
export type StaffProgressTableProps = {
    onBack?: () => void,
    headerActions: HeaderActionType[]
    onSelectedPerson: (id: string, action: string) => void,
    isClientPortal?: boolean,
    company: CompanyDataType,
    onHeaderAction: (action: StaffProgressHeaderActionType) => void,
    myId: string | null
}
const StaffProgressTable = ({ onBack, onSelectedPerson, myId, isClientPortal, company, onHeaderAction, headerActions }: StaffProgressTableProps) => {
    const { cid } = useParams()
    const { loading, data } = useQuery(GET_PEOPLE_WITH_SESSION_COUNT, {
        variables: {
            id: cid,
            query: "",
            first: 100,
            after: null

        },
        notifyOnNetworkStatusChange: true
    })

    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[],
        rows: GridRowModel[],
        filteredRows: GridRowModel[];
        selectionModel: (string | number)[];
        // selectionModel: GridRowSelectionModel;
        selections: {
            initial: GridRowId[],
            pageAssign: GridRowId[],
            prevAssign: GridRowId[],
            nextAssign: GridRowId[],
            unassign: GridRowId[],
            allSelected: GridRowId[]
        };
        status: "NORMAL" | "FILTERING";

    }>({
        columns: [],
        rows: [],
        filteredRows: [],
        selectionModel: [],
        // selectionModel: { type: "include", ids: new Set() },
        selections: {
            initial: [],
            pageAssign: [], // Contains student IDs that have a relationship with person
            prevAssign: [], // Contains student IDs across page/table
            nextAssign: [], // Contains student IDs used to created a person/student relationship
            unassign: [],   // Contains student IDs used to remove the person/student relationship
            allSelected: [] // Contains student IDs for selections on table/page
        },
        status: "NORMAL",
    })



    useEffect(() => {
        setPageTableState((prevState) => {
            const columns = [...TABLE_COLUMNS]
            columns.push({
                field: "profile", headerName: "Profile", headerAlign: "center",
                align: "center",
                sortable: false,
                flex: .5,
                renderCell: (params) => {
                    return (
                        <IconButton onClick={() => onSelectedPerson(params.row.id, "Profile")}>
                            <FolderShared />
                        </IconButton>
                    );
                }
            })
            if (!isClientPortal) {
                columns.push({
                    align: "center",
                    headerAlign: "center",
                    sortable: false,
                    resizable: false,
                    editable: false,
                    disableColumnMenu: true,
                    field: "id",
                    width: 80,
                    headerName: "Expire",
                    renderCell: (params) => {
                        const status = params.row.expired
                        const isSelf = params.row.id === myId;
                        const role = params.row.role.normalizedName
                        // const userDoesNotHaveAccess = UserRoleHierarchy[myHighestRole as keyof typeof UserRoleHierarchy] < UserRoleHierarchy[role as keyof typeof UserRoleHierarchy]
                        let disabledMessage;
                        if (isSelf) {
                            disabledMessage = "Cannot Archive Self"
                        } else if (status) {
                            disabledMessage = "User already archived"
                        } else if (role === "SUPER ADMIN") {
                            disabledMessage = "Warning this is a super Admin"
                        }
                        // else if (userDoesNotHaveAccess) {
                        //     disabledMessage = "Cannot archive user with more permissions"
                        // }
                        else {
                            disabledMessage = ""
                        }

                        return (
                            <Tooltip title={disabledMessage}>
                                <div>
                                    <IconButton onClick={() => onSelectedPerson(params.row.id, "EXPIRE")}>
                                        <Close
                                            style={{
                                                backgroundColor: status || isSelf ? "#EEE" : "#8E1C3B",
                                                borderRadius: "100%",
                                                color: status || isSelf ? "#999" : "#FFF",
                                            }}
                                        />
                                    </IconButton>
                                </div>
                            </Tooltip>
                        )
                    }
                })
            }
            const rows = orderBySessionsRun(data?.personStatsPerCompany?.edges.map(mapToTable));
            return {
                ...prevState,
                columns,
                rows
            }
        })
    }, [loading, data, pageTableState.selectionModel])
    const handleFilter = (selectedOption: StandardFilterOption) => {
        setPageTableState((prevState) => {
            const filteredRows = selectedOption === "ACTIVE"
                ? prevState.rows.filter((x) => !x.expired)
                : selectedOption == "EXPIRED"
                    ? prevState.rows.filter((x) => x.expired)
                    : prevState.rows; // Default "ALL"

            return {
                ...prevState,
                status: selectedOption === "ALL" ? "NORMAL" : "FILTERING",
                filteredRows,
            };
        });
    }

    const isRowSelectable = (person: any): boolean => {
        const status = !person.expired //if they are expired it returns true
        //TODO: FIND AN OPT FOR IF THERE IS A STUDENT IN THEIR CARE THAT ONLY HAS THEM
        const maxReached = pageTableState.selectionModel.length < 31
        const isSelectabled = status && maxReached
        return isSelectabled
    }






    return (
        <TableStandard
            loading={!company}
            renderToolbar={(props: any) => {
                return isClientPortal ?
                    (
                        <TableToolBar title="Staff Insights" subTitle={`@${company?.name}`} onBack={onBack}
                            {...props}
                            onFilter={handleFilter}
                        />
                    ) :
                    (
                        <Box component="div" >
                            <TableToolBarStacked title="Staff Progress" subTitle={`@${company?.name}`} onBack={onBack} {...props}
                                headerActions={headerActions} />
                        </Box>
                    )
            }}
            columns={pageTableState.columns}
            rows={pageTableState.rows}
            disableBoxShadow
            pageSize={100}
            autoHeight
            getRowClassName={(params) => params.row.expired ? "Expired" : "Active"}

        />
    )
}

export default StaffProgressTable